import { useState, useEffect } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { TYPO } from "styles/Typo";
import { MembershipRecommandToast } from "components/atoms/CustomToast";
import Image from "next/image";
import useHandleImageError from "utils/useHandleImageError";
import { getStockLogo } from "utils/image";
import { routeToStockDetailPage } from "utils/routing";
import { Forward } from "components/common/Icons";
import { useTabTracking } from "lib/analytics/react/hooks";
import { MONTH_ENG } from "utils/constant/ETC";
function TotalLevel({
  detailData,
  scoreData,
}: {
  detailData: any;
  scoreData?: any;
}) {
  const { t } = useTranslation("detail");
  const router = useRouter();
  // Recoil 로딩 게이트는 SSR 에서 항상 true 라 GEO 본문이 비므로,
  // 점수 데이터 유무로만 분기한다 (GSSP fallback / SWR).
  const totalPercent = scoreData?.TOTAL_PER;
  const BASE_DT = scoreData?.BASE_DT?.split("T")[0];

  const month = Number(BASE_DT?.split("-")[1]);
  const day = Number(BASE_DT?.split("-")[2]);
  const date =
    month && day
      ? router.locale === "ko"
        ? `${month}월 ${day}일`
        : `${MONTH_ENG[month - 1]} ${day}`
      : "";

  const handleImageError = useHandleImageError();

  const [loginNotiToastVisible, setLoginNotiToastVisible] = useState(false);
  const { trackClick } = useTabTracking("stock_tab_click", [
    "overview",
    "score",
    "fund",
    "feed",
  ]);
  const diagnosticItem = [
    {
      name: t("industryMomentum"),
      engName: "Industry Momentum",
      link: `/detail/${detailData?.ITEM_CD_DL}/mmt`,
      status: scoreData?.MOMENTUM ? scoreData?.MOMENTUM === "높음" : undefined,
      score: scoreData?.MOM_PER,
    },
    {
      name: t("returnPerformance"),
      engName: "Return Performance",
      link: `/detail/${detailData?.ITEM_CD_DL}/sharpe`,
      status: scoreData?.SHARPE_RATIO
        ? scoreData?.SHARPE_RATIO === "높음"
        : undefined,
      score: scoreData?.RAR_PER,
    },
    {
      name: t("financialSoundness"),
      engName: "Financial Soundness",
      link: `/detail/${detailData?.ITEM_CD_DL}/fin`,
      status: scoreData?.FINANCIAL
        ? scoreData?.FINANCIAL === "높음"
        : undefined,
      score: scoreData?.FIN_SCORE,
    },
  ];

  if (!detailData && !scoreData) return null;
  if (!totalPercent && String(totalPercent) !== "0")
    return (
      <div className="pt-[30px] px-6">
        <div className="bg-gray2 rounded-[14px] flex border border-gray15 overflow-hidden p-4 gap-3 items-center">
          <Image
            unoptimized
            quality={100}
            alt="img"
            height={40}
            width={40}
            src={getStockLogo(detailData?.ITEM_CD_DL)}
            style={{
              borderRadius: "12px",
              objectFit: "cover",
              width: "48px",
              height: "48px",
            }}
            onError={(event) => handleImageError(event, "")}
            priority={true}
          />
          <div className="flex flex-col">
            <p className={`${TYPO.TYPO8_RG} text-gray80`}>투자매력도</p>
            <p className={`${TYPO.TYPO5_1_MD} text-gray50`}>
              아직 정보가 없어요
            </p>
          </div>
        </div>
      </div>
    );

  return (
    <>
      <div className="bg-white flex flex-col  ">
        <div className="px-6 py-[30px] flex flex-col gap-1 !pb-1">
          <div className="bg-gray2 rounded-[14px] flex flex-col border border-gray15 overflow-hidden">
            <div className="p-4 flex gap-3 items-center">
              <Image
                unoptimized
                quality={100}
                alt="img"
                height={40}
                width={40}
                src={getStockLogo(detailData?.ITEM_CD_DL)}
                style={{
                  borderRadius: "12px",
                  objectFit: "cover",
                  width: "48px",
                  height: "48px",
                }}
                onError={(event) => handleImageError(event, "")}
                priority={true}
              />
              <div className="flex flex-col grow">
                <p className={`${TYPO.TYPO7_1_MD} text-gray60`}>
                  {t("totalAttractiveness")}
                </p>
                <p className={`${TYPO.TYPO6_SB} text-gray80`}>
                  {t("scorePoints", { score: totalPercent })}
                </p>
              </div>
            </div>
            {diagnosticItem.map((item, index) => (
              <div
                key={index}
                className={` py-[10px] px-4 flex items-center justify-between w-full  border-t border-gray15 bg-white`}
              >
                <span className={`${TYPO.TYPO7_1_RG} text-gray70 `}>
                  {item.name}
                </span>

                <div className="px-[6px] py-[2px] bg-gray5 rounded-[4px]">
                  <p className={`${TYPO.TYPO7_1_MD} text-gray80 `}>
                    {item?.score}
                  </p>
                </div>
              </div>
            ))}
            <div
              data-gtm-id={"sDetail-score-more-button"}
              className="w-full flex items-center justify-center cursor-pointer px-4 py-[10px] gap-1 border-t border-hairlineBorder"
              onClick={() => {
                trackClick(
                  "score",
                  "shortcut",
                  "stock_overview_attractiveness"
                );
                routeToStockDetailPage(
                  router,
                  detailData?.ITEM_CD_DL,
                  "push",
                  {
                    shallow: true,
                  },
                  "score"
                );
              }}
            >
              <p className={`${TYPO.TYPO7_MD} text-gray80`}>
                {t("viewMoreAnalysis")}
              </p>
              <Forward fill="gray80" width={12} height={12} />
            </div>
          </div>
          <div className="flex justify-end items-center">
            <p className={`${TYPO.TYPO8_RG} text-gray40`}>
              {t("lastUpdated", { date })}
            </p>
          </div>
          <MembershipRecommandToast
            message={t("youCanAccessThisAfterSignUp")}
            isVisible={loginNotiToastVisible}
            onClose={() => setLoginNotiToastVisible(false)}
            isSuccess={false}
            width={331}
            buttonHanlder={() => {
              router.push(`/login?from=${router.asPath}`);
            }}
          />
        </div>
      </div>
    </>
  );
}

export default TotalLevel;
